import { forwardRef, useState, type ComponentPropsWithRef } from "react";

import { Command } from "cmdk";
import { orderBy } from "lodash";

import {
  type AirHandlerUnit as AirHandlerUnitType,
  type BoilerRoom as BoilerRoomType,
  type Floor,
  type Gateway as GatewayType,
  type PatchBuildingRoomsRequestBody,
  type Room as RoomType,
  type UpdateAirHandlerUnitRequestBody,
  type UpdateBoilerRoomRequestBody,
} from "@eisox/api/core-api";
import {
  ActionButton,
  Circle,
  cn,
  DropdownMenu,
  Field,
  IconButton,
  Modal,
  Plan,
  Radio,
  ScrollArea,
  Select,
  TextField,
  type CircleProps,
} from "@eisox/design-system";
import { useAppForm } from "@eisox/form";
import {
  AirHandlerUnitIcon,
  BoilerRoomIcon,
  GatewayIcon,
  MultisensorIcon,
  OptionIcon,
  PencilIcon,
  RoomIcon,
  ValveIcon,
} from "@eisox/icons";
import { getDirtyValues } from "@eisox/tools";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useSelector } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { airHandlerUnitApi, boilerRoomApi, gatewayApi, roomApi, valveApi } from "~/api";
import { floorPlanQueries } from "~/features/floor/floor-plan.queries";
import { m } from "~/paraglide/messages";
import type { EnhancedGateway } from "~/types/gateway.type";
import type { EnhancedMultisensor } from "~/types/multisensor.type";
import type { EnhancedValve } from "~/types/valve.type";

import { AirHandlerUnit } from "./air-handler-unit";
import { BoilerRoom } from "./boiler-room";
import { ExpandedGateway } from "./expended-gateway";
import { ExpandedRoom } from "./expended-room";
import { Gateway } from "./gateway";
import { Multisensor } from "./multisensor";
import { Room } from "./room";
import { Valve } from "./valve";

const NAME = "MoveItemDialog";

interface MoveItemDialogProps extends Modal.RootProps {
  buildingId: string;
  type: "room" | "valve" | "multisensor" | "gateway" | "boilerRoom" | "airHandlerUnit";
  items: (RoomType | EnhancedValve | EnhancedMultisensor | EnhancedGateway | BoilerRoomType | AirHandlerUnitType)[];
  floors: Floor[];
  gateways?: GatewayType[];
  rooms?: RoomType[];
  defaultSelectedItemId?: string;
  initialFloorId?: string;
  children?: React.ReactNode;
}

export const MoveItemDialog: React.FC<MoveItemDialogProps> = ({
  buildingId,
  type,
  items,
  floors,
  gateways,
  rooms,
  defaultSelectedItemId,
  initialFloorId,
  children,
  open: openProp,
  onOpenChange,
  defaultOpen,
  ...props
}) => {
  const queryClient = useQueryClient();

  const [open, setOpen] = useControllableState({
    prop: openProp,
    onChange: onOpenChange,
    defaultProp: defaultOpen ?? false,
  });
  const [selectedItemId, setSelectedItemId] = useState<string | undefined>(defaultSelectedItemId);
  const [selectedFloorId, setSelectedFloorId] = useState<string | undefined>(
    items.find(i => i.id === defaultSelectedItemId)?.floor?.id ?? initialFloorId ?? floors[0]?.id,
  );
  const [sortBy, setSortBy] = useState<string>(SORTS[type][0] ?? "name");
  const [hovered, setHovered] = useState<{ room?: string; gateway?: string }>({});

  const { data: plan } = useQuery({
    ...floorPlanQueries.byId(selectedFloorId ?? ""),
    enabled: !!selectedFloorId,
  });

  const { mutateAsync: updateRooms } = useMutation({
    mutationFn: (data: PatchBuildingRoomsRequestBody) => roomApi.update(buildingId, data),
    onSuccess: async () => {
      await queryClient.invalidateQueries(roomApi.queries.building(buildingId));
    },
  });

  const { mutateAsync: updateAirHandlerUnits } = useMutation({
    mutationFn: async (moves: { id: string; body: UpdateAirHandlerUnitRequestBody }[]) => {
      const results = await Promise.allSettled(
        moves.map(move => airHandlerUnitApi.update(buildingId, move.id, move.body)),
      );
      await queryClient.invalidateQueries({ queryKey: airHandlerUnitApi.queries._def });
      const firstFailure = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
      if (firstFailure) throw firstFailure.reason;
    },
  });

  const { mutateAsync: updateBoilerRooms } = useMutation({
    mutationFn: async (moves: { id: string; body: UpdateBoilerRoomRequestBody }[]) => {
      const results = await Promise.allSettled(moves.map(move => boilerRoomApi.update(move.id, move.body)));
      await queryClient.invalidateQueries(boilerRoomApi.queries.building(buildingId));
      const firstFailure = results.find((result): result is PromiseRejectedResult => result.status === "rejected");
      if (firstFailure) throw firstFailure.reason;
    },
  });

  const { mutateAsync: updateValvesMut } = useMutation({
    mutationFn: (body: Parameters<typeof valveApi.update>[1]) => valveApi.update(buildingId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: valveApi.queries.building(buildingId).queryKey });
    },
  });

  const { mutateAsync: updateGatewaysMut } = useMutation({
    mutationFn: (body: Parameters<typeof gatewayApi.update>[1]) => gatewayApi.update(buildingId, body),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: gatewayApi.queries.building(buildingId).queryKey });
    },
  });

  const form = useAppForm({
    defaultValues: {
      items: items.filter(item => item.floor !== undefined),
    },
    onSubmit: async ({ value, formApi }) => {
      const dirty = getDirtyValues(formApi.options.defaultValues, value, { identity: "id" });
      if (!dirty?.items) return;
      const dirtyItems = dirty.items;

      const positionPatch = (floor: { id?: string; x?: number; y?: number } | undefined) => ({
        ...(floor?.id !== undefined && { floorId: floor.id }),
        ...(floor?.x !== undefined && { x: floor.x }),
        ...(floor?.y !== undefined && { y: floor.y }),
      });

      if (type === "room") {
        await updateRooms(dirtyItems.map(item => ({ ids: [item.id], ...positionPatch(item.floor) })));
      } else if (type === "airHandlerUnit") {
        await updateAirHandlerUnits(dirtyItems.map(item => ({ id: item.id, body: positionPatch(item.floor) })));
      } else if (type === "boilerRoom") {
        await updateBoilerRooms(dirtyItems.map(item => ({ id: item.id, body: positionPatch(item.floor) })));
      } else if (type === "valve") {
        await updateValvesMut(
          dirtyItems.map(item => ({
            ids: [item.id],
            ...positionPatch(item.floor),
            ...("roomId" in item && { roomId: item.roomId }),
            ...("gatewayId" in item && { gatewayId: item.gatewayId }),
            ...("heatingNetworkId" in item && { heatingNetworkId: item.heatingNetworkId }),
          })),
        );
      } else if (type === "gateway") {
        await updateGatewaysMut(dirtyItems.map(item => ({ ids: [item.id], ...positionPatch(item.floor) })));
      }
      formApi.reset();
      setOpen(false);
    },
  });

  const currentItems = useSelector(form.store, state => state.values.items);

  const selectedItemIndex = currentItems.findIndex(item => item.id === selectedItemId);
  const selectedItem = currentItems.find(item => item.id === selectedItemId);
  const sortedItems = orderBy(currentItems, [sortBy], "asc");
  const itemsOnFloor = currentItems.filter(item => item.floor?.id === selectedFloorId);

  // Valves are positioned relative to their room and gateway, so both are shown as context on the plan.
  const isValve = type === "valve";
  const selectedValve = isValve ? (selectedItem as EnhancedValve | undefined) : undefined;
  const gatewaysOnFloor = isValve ? (gateways ?? []).filter(gateway => gateway.floor.id === selectedFloorId) : [];
  const roomsOnFloor = isValve ? (rooms ?? []).filter(room => room.floor.id === selectedFloorId) : [];

  const gatewayOptions = gateways?.map(gateway => ({ value: gateway.id, name: gateway.name })) ?? [];
  const roomOptions = rooms?.map(room => ({ value: room.id, name: room.name })) ?? [];

  // cmdk matches on these instead of the item id, which is what `value` carries to stay unique.
  const searchKeywords = (item: (typeof currentItems)[number]): string[] => {
    switch (type) {
      case "valve": {
        const valve = item as EnhancedValve;
        return [String(valve.uid), valve.mac, valve.roomName].filter((v): v is string => v != null);
      }
      case "multisensor": {
        const multisensor = item as EnhancedMultisensor;
        return [String(multisensor.uid), multisensor.mac, multisensor.room.name].filter((v): v is string => v != null);
      }
      case "gateway": {
        const gateway = item as EnhancedGateway;
        return [gateway.name, String(gateway.uid), gateway.mac].filter((v): v is string => v != null);
      }
      default:
        return [(item as RoomType | BoilerRoomType | AirHandlerUnitType).name];
    }
  };

  const handleChangeSort = (sort: string) => setSortBy(sort);
  const handleSelectItem = (id: string) => {
    setSelectedItemId(id);
    const item = currentItems.find(i => i.id === id);
    if (item?.floor?.id && item.floor.id !== selectedFloorId) {
      setSelectedFloorId(item.floor.id);
    }
  };

  const handleChangeFloor = (floorId: string) => {
    setSelectedFloorId(floorId);
    if (selectedItemIndex !== -1) {
      form.setFieldValue(`items[${selectedItemIndex}].floor.id`, floorId);
    }
  };

  // Rooms are floor-specific: reassigning to a room on another floor follows the valve to that plan.
  const handleChangeRoom = (roomId: string) => {
    const room = rooms?.find(r => r.id === roomId);
    if (!room || !selectedItem) return;
    form.setFieldValue(`items[${selectedItemIndex}].roomId`, roomId);
    if (room.floor.id !== selectedItem.floor?.id) {
      form.setFieldValue(`items[${selectedItemIndex}].floor.id`, room.floor.id);
      form.setFieldValue(`items[${selectedItemIndex}].floor.x`, room.floor.x);
      form.setFieldValue(`items[${selectedItemIndex}].floor.y`, room.floor.y);
      setSelectedFloorId(room.floor.id);
    }
  };

  return (
    <Modal.Root {...props} open={open} onOpenChange={setOpen}>
      <Modal.Trigger asChild>{children}</Modal.Trigger>
      <Modal.Content asChild>
        <form
          className="h-225 w-325 flex flex-col overflow-hidden"
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            void form.handleSubmit();
          }}
        >
          <form.AppForm>
            <Modal.Header
              title={m.move_item_dialog_title({ type })}
              subtitle={m.move_item_dialog_subtitle({ type })}
              icon={ICONS[type]}
            >
              <Modal.Close asChild>
                <ActionButton variant="cancel">{m.cancel()}</ActionButton>
              </Modal.Close>
              <form.SubmitButton label={m.save()} icon={<PencilIcon />} />
            </Modal.Header>
            <div className={"min-h-0 gap-10 flex flex-1"}>
              <div className={"gap-2.5 flex flex-1 flex-col"}>
                <FloorSelect
                  className="max-w-87.5"
                  floors={floors}
                  value={selectedFloorId}
                  onValueChange={setSelectedFloorId}
                />
                <div className="relative w-full flex-1">
                  <Plan.Root>
                    <Plan.Controls className="top-2.5 left-2.5 absolute z-1" />
                    <Plan.Wrapper className="h-full! w-full!" plan={plan}>
                      {gatewaysOnFloor.map(gateway => (
                        <Plan.Item
                          key={gateway.id}
                          position={{ x: gateway.floor.x, y: gateway.floor.y }}
                          origin="bottom center"
                        >
                          <ExpandedGateway
                            selected={gateway.id === selectedValve?.gatewayId}
                            className={cn(
                              "transition-transform duration-500",
                              hovered.gateway === gateway.id && "scale-[2]",
                            )}
                          >
                            {gateway.name}
                          </ExpandedGateway>
                        </Plan.Item>
                      ))}
                      {roomsOnFloor.map(room => (
                        <Plan.Item key={room.id} position={{ x: room.floor.x, y: room.floor.y }} origin="bottom center">
                          <ExpandedRoom
                            selected={room.id === selectedValve?.roomId}
                            className={cn("transition-transform duration-500", hovered.room === room.id && "scale-[2]")}
                          >
                            {room.name}
                          </ExpandedRoom>
                        </Plan.Item>
                      ))}
                      {itemsOnFloor.map(item => {
                        const currentIndex = currentItems.findIndex(i => i.id === item.id);
                        const floor = item.floor;
                        if (!floor) return null;
                        return (
                          <Plan.Item
                            key={item.id}
                            asChild
                            draggable
                            position={{ x: floor.x, y: floor.y }}
                            onClick={() => setSelectedItemId(item.id)}
                            onPositionChange={() => setSelectedItemId(item.id)}
                            onPositionEnd={({ x, y }) => {
                              form.setFieldValue(`items[${currentIndex}].floor.x`, x);
                              form.setFieldValue(`items[${currentIndex}].floor.y`, y);
                            }}
                          >
                            <Item type={type} selected={item.id === selectedItemId}>
                              {isValve ? (item as EnhancedValve).uid : undefined}
                            </Item>
                          </Plan.Item>
                        );
                      })}
                    </Plan.Wrapper>
                  </Plan.Root>
                </div>
              </div>
              <div className={"w-68.75 flex flex-col"}>
                <Command
                  className={"min-h-0 gap-2.5 flex flex-1 flex-col"}
                  filter={(_, search, keywords) =>
                    (keywords ?? []).join(" ").toLowerCase().includes(search.toLowerCase()) ? 1 : 0
                  }
                >
                  <div className={"gap-5 flex items-center"}>
                    <Command.Input asChild className="flex-1">
                      <TextField.Root placeholder={isValve ? m.valve_move_search() : m.name()} />
                    </Command.Input>
                    <DropdownMenu.Root>
                      <DropdownMenu.Trigger asChild>
                        <IconButton>
                          <OptionIcon />
                        </IconButton>
                      </DropdownMenu.Trigger>
                      <DropdownMenu.Content align="end" sideOffset={10} style={{ zIndex: 2000 }}>
                        <DropdownMenu.RadioGroup value={sortBy} onValueChange={handleChangeSort}>
                          <DropdownMenu.Label>{m.sort()}</DropdownMenu.Label>
                          {SORTS[type].map((s, i) => (
                            <DropdownMenu.RadioItem key={i} value={s}>
                              <DropdownMenu.ItemIndicator asChild forceMount>
                                <Radio checked={s === sortBy} style={{ marginRight: 10 }} />
                              </DropdownMenu.ItemIndicator>
                              <p>{SORT_LABELS[s as keyof typeof SORT_LABELS]}</p>
                            </DropdownMenu.RadioItem>
                          ))}
                        </DropdownMenu.RadioGroup>
                      </DropdownMenu.Content>
                    </DropdownMenu.Root>
                  </div>
                  <ScrollArea>
                    <Command.List className={"min-h-0 my-2.5 flex flex-1 flex-col text-base outline-none"}>
                      {sortedItems.map(item => (
                        <Command.Item
                          key={item.id}
                          asChild
                          className="mb-1.25 rounded-[5px] p-[7.5px] data-[selected=true]:bg-primary-gray"
                          value={item.id}
                          keywords={searchKeywords(item)}
                          onSelect={() => handleSelectItem(item.id)}
                          onMouseEnter={() =>
                            setHovered(
                              isValve
                                ? {
                                    room: (item as EnhancedValve).roomId,
                                    gateway: (item as EnhancedValve).gatewayId,
                                  }
                                : {},
                            )
                          }
                          onMouseLeave={() => setHovered({})}
                        >
                          <DrawerListItem
                            type={type}
                            item={item}
                            floors={floors}
                            selected={item.id === selectedItemId}
                          />
                        </Command.Item>
                      ))}
                      <Command.Empty>{m.no_result()}</Command.Empty>
                    </Command.List>
                  </ScrollArea>
                </Command>
                <div className="gap-5 p-5 flex max-h-[65%] flex-col rounded-[10px] bg-primary-gray">
                  <DrawerListItem type={type} item={selectedItem} floors={floors} />
                  {type === "valve" ? (
                    <form.AppField name={`items[${selectedItemIndex}].roomId`}>
                      {field => (
                        <field.Select
                          classNames={{ control: "w-full" }}
                          label={m.valve_move_change_room()}
                          placeholder={m.select_room()}
                          multiple={false}
                          options={roomOptions}
                          onValueChange={value => handleChangeRoom(value as string)}
                        />
                      )}
                    </form.AppField>
                  ) : (
                    <FloorSelect
                      floors={floors}
                      value={selectedItem?.floor?.id}
                      onValueChange={handleChangeFloor}
                      side="left"
                      align="end"
                    />
                  )}
                  {type === "valve" && (
                    <form.AppField name={`items[${selectedItemIndex}].gatewayId`}>
                      {field => (
                        <field.Select
                          classNames={{ control: "w-full" }}
                          label={m.valve_move_change_gateway()}
                          placeholder={m.select_gateway()}
                          multiple={false}
                          options={gatewayOptions}
                        />
                      )}
                    </form.AppField>
                  )}
                </div>
              </div>
            </div>
          </form.AppForm>
        </form>
      </Modal.Content>
    </Modal.Root>
  );
};

MoveItemDialog.displayName = NAME;

interface DrawerListItemProps extends ComponentPropsWithRef<"div"> {
  type: "room" | "valve" | "multisensor" | "gateway" | "boilerRoom" | "airHandlerUnit";
  item?: RoomType | EnhancedValve | EnhancedMultisensor | EnhancedGateway | BoilerRoomType | AirHandlerUnitType;
  floors: Floor[];
  selected?: boolean;
}

const DrawerListItem = forwardRef<HTMLDivElement, DrawerListItemProps>(
  ({ type, item, floors, selected, className, ...props }, ref) => {
    const title = (): string => {
      switch (type) {
        case "room":
          return (item as RoomType | undefined)?.name ?? "-";
        case "valve": {
          const valve = item as EnhancedValve | undefined;
          return valve ? `${valve.uid} - ${valve.mac}` : "-";
        }
        case "multisensor": {
          const multisensor = item as EnhancedMultisensor | undefined;
          return multisensor ? `${multisensor.uid} - ${multisensor.mac}` : "-";
        }
        case "gateway":
          return (item as EnhancedGateway | undefined)?.name ?? "-";
        case "boilerRoom":
          return (item as BoilerRoomType | undefined)?.name ?? "-";
        case "airHandlerUnit":
          return (item as AirHandlerUnitType | undefined)?.name ?? "-";
      }
    };

    const subtitle = (): string | undefined => {
      switch (type) {
        case "room":
          return floors.find(floor => floor.id === (item as RoomType | undefined)?.floor.id)?.name;
        case "valve":
          return (item as EnhancedValve | undefined)?.roomName ?? "-";
        case "multisensor":
          return (item as EnhancedMultisensor | undefined)?.room.name ?? "-";
        case "gateway":
          return (item as EnhancedGateway | undefined)?.mac ?? "-";
        case "boilerRoom":
          return floors.find(floor => floor.id === (item as BoilerRoomType | undefined)?.floor?.id)?.name;
        case "airHandlerUnit":
          return floors.find(floor => floor.id === (item as AirHandlerUnitType | undefined)?.floor?.id)?.name;
      }
    };

    return (
      <div ref={ref} {...props} className={cn("gap-5 flex items-center", className)}>
        <Circle.Root
          backgroundColor={selected === undefined ? "white" : selected ? "blue" : "gray"}
          color={selected === undefined ? "darkBlue" : selected ? "white" : "darkBlue"}
        >
          {ICONS[type]}
        </Circle.Root>
        <div className="[&_p:first-child]:font-medium [&_p:first-child]:text-base [&_p:first-child]:text-nowrap [&_p:last-child]:text-sm [&_p:last-child]:text-primary-dark-gray">
          <p>{title()}</p>
          <p>{subtitle()}</p>
        </div>
      </div>
    );
  },
);

interface FloorSelectProps {
  floors: Floor[];
  value: string | undefined;
  onValueChange: (value: string) => void;
  side?: Select.ContentProps["side"];
  align?: Select.ContentProps["align"];
  className?: string;
}

const FloorSelect = ({ floors, value, onValueChange, side, align, className }: FloorSelectProps) => {
  return (
    <Field.Root className={className}>
      <Field.Label>{m.level()}</Field.Label>
      <Select.Root multiple={false} value={value} onValueChange={onValueChange}>
        <Select.Trigger className="w-full">
          <Select.Value />
        </Select.Trigger>
        <Select.Content side={side} align={align}>
          <Select.Viewport>
            {floors.map(floor => (
              <Select.Item key={floor.id} value={floor.id}>
                {floor.name}
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Root>
    </Field.Root>
  );
};

interface ItemProps extends Omit<CircleProps, "type"> {
  type: "room" | "valve" | "multisensor" | "gateway" | "boilerRoom" | "airHandlerUnit";
}

const Item: React.FC<ItemProps> = ({ type, ...props }) => {
  switch (type) {
    case "valve":
      return <Valve {...props} />;
    case "multisensor":
      return <Multisensor {...props} />;
    case "room":
      return <Room {...props} />;
    case "gateway":
      return <Gateway {...props} />;
    case "boilerRoom":
      return <BoilerRoom {...props} />;
    case "airHandlerUnit":
      return <AirHandlerUnit {...props} />;
    default:
      return null;
  }
};

const ICONS = {
  room: <RoomIcon />,
  valve: <ValveIcon />,
  multisensor: <MultisensorIcon />,
  gateway: <GatewayIcon />,
  boilerRoom: <BoilerRoomIcon />,
  airHandlerUnit: <AirHandlerUnitIcon />,
};

const SORTS = {
  room: ["name"],
  valve: ["uid", "mac", "roomName"],
  multisensor: ["uid", "mac", "roomName"],
  gateway: ["name", "uid", "meshes", "mac"],
  boilerRoom: ["name"],
  airHandlerUnit: ["name"],
};

const SORT_LABELS = {
  name: m.name(),
  uid: m.index(),
  meshes: m.meshes(),
  mac: m.mac(),
  roomName: m.room(),
};
